import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowLeft, Lock, Trash2, Image as ImageIcon, Edit3 } from "lucide-react";

const ALL_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const ALL_SLOTS = ["breakfast", "lunch", "snacks", "dinner"];
const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

const cellKey = (day, slot) => `${day}_${slot}`;

function TemplateCard({ tpl, onEdit, onDelete, onToggleActive }) {
  const isActive = tpl.active !== false;
  return (
    <div data-testid={`tpl-card-${tpl.id}`}
      className={`bg-white border rounded-2xl p-5 hover:shadow-md transition-all ${isActive ? "border-border" : "border-amber-200 bg-amber-50/30"}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-heading text-lg font-extrabold text-ink tracking-tight">{tpl.name}</h3>
          <div className="text-[10px] text-ink-500 mt-0.5">Updated {tpl.updated_at?.slice(0, 10) || "—"}</div>
        </div>
        <div className="flex items-center gap-1">
          {!isActive && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-200 text-slate-600">
              Inactive
            </span>
          )}
          {tpl.locked && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-100 text-amber-700">
              <Lock size={10} /> Live
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {(tpl.slots || []).map(s => (
          <span key={s} className="text-[10px] font-semibold uppercase px-2 py-0.5 bg-cream-200 text-ink-700 rounded">{s}</span>
        ))}
      </div>
      {/* mini-preview */}
      <div className="grid gap-0.5 mb-4" style={{ gridTemplateColumns: `repeat(${(tpl.days || []).length}, 1fr)` }}>
        {(tpl.days || []).map(d => (
          <div key={d} className="text-center">
            <div className="text-[9px] uppercase font-bold text-ink-500">{DAY_LABELS[d]}</div>
            <div className="flex flex-col gap-0.5 mt-0.5">
              {(tpl.slots || []).map(sl => {
                const c = (tpl.cells || {})[cellKey(d, sl)];
                const filled = c && Array.isArray(c.items) && c.items.length > 0;
                return <div key={sl} className={`h-2 rounded ${filled ? "bg-emerald-400" : "bg-cream-200"}`} />;
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="text-xs text-ink-500 mb-3 min-h-[1.2rem]">
        {tpl.usage?.length > 0 ? (
          <span>Linked to <strong>{tpl.usage.length}</strong> plan{tpl.usage.length > 1 ? "s" : ""}: {tpl.usage.map(u => u.plan_name).join(", ")}</span>
        ) : (
          <span className="text-ink-400">Unassigned</span>
        )}
      </div>
      <div className="flex gap-2">
        <button data-testid={`tpl-edit-${tpl.id}`} onClick={() => onEdit(tpl)}
          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-ink text-white hover:bg-ink-700">
          <Edit3 size={11} /> {tpl.locked ? "View" : "Edit"}
        </button>
        <button data-testid={`tpl-toggle-active-${tpl.id}`} onClick={() => onToggleActive(tpl)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold ${(tpl.active !== false) ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}
          title={(tpl.active !== false) ? "Set inactive" : "Set active"}>
          {(tpl.active !== false) ? "Active" : "Inactive"}
        </button>
        <button data-testid={`tpl-delete-${tpl.id}`} onClick={() => onDelete(tpl)} disabled={tpl.locked}
          className="px-3 py-1.5 rounded-full text-xs font-semibold bg-cream-200 text-brand hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed">
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}

function CellEditor({ value, onChange, locked }) {
  const items = Array.isArray(value?.items) ? value.items : [];
  const update = (patch) => onChange({ ...(value || {}), ...patch });

  return (
    <div className="bg-white border border-border rounded-lg p-2 min-h-[110px] text-xs flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <button onClick={() => update({ veg: !(value?.veg ?? true) })} disabled={locked}
          className={`w-4 h-4 rounded-sm border-2 ${value?.veg === false ? "border-rose-600" : "border-emerald-600"} flex items-center justify-center disabled:opacity-50`}>
          <div className={`w-2 h-2 rounded-full ${value?.veg === false ? "bg-rose-600" : "bg-emerald-600"}`} />
        </button>
        <input type="url" placeholder="img url" value={value?.image_url || ""} onChange={e => update({ image_url: e.target.value })}
          disabled={locked} className="text-[10px] text-ink-500 bg-transparent w-20 focus:outline-none" />
      </div>
      <textarea
        rows={4}
        placeholder="Dal&#10;Rice&#10;Veg curry"
        value={items.join("\n")}
        onChange={e => update({ items: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
        disabled={locked}
        className="w-full text-xs bg-cream-100 rounded p-1 resize-none focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-cream-50"
      />
      {value?.image_url && (
        <img src={value.image_url} alt="" className="w-full h-12 object-cover rounded mt-1"
          onError={(e) => { e.target.style.display = "none"; }} />
      )}
    </div>
  );
}

function TemplateBuilder({ tpl, onClose, onSaved }) {
  const [draft, setDraft] = useState({
    id: tpl?.id,
    name: tpl?.name || "",
    days: tpl?.days || ["mon", "tue", "wed", "thu", "fri"],
    slots: tpl?.slots || ["breakfast", "lunch", "snacks"],
    cells: tpl?.cells || {},
    notes: tpl?.notes || "",
    locked: !!tpl?.locked,
    usage: tpl?.usage || [],
  });
  const [saving, setSaving] = useState(false);
  const [showLockConfirm, setShowLockConfirm] = useState(false);

  const updateCell = (day, slot, val) => {
    setDraft(prev => ({ ...prev, cells: { ...prev.cells, [cellKey(day, slot)]: val } }));
  };

  const toggleDay = (d) => setDraft(p => ({ ...p, days: p.days.includes(d) ? p.days.filter(x => x !== d) : [...p.days, d] }));
  const toggleSlot = (s) => setDraft(p => ({ ...p, slots: p.slots.includes(s) ? p.slots.filter(x => x !== s) : [...p.slots, s] }));

  const doSave = async () => {
    if (!draft.name.trim()) return toast.error("Template name required");
    setSaving(true);
    try {
      await api.post("/weekly-menu/templates", {
        id: draft.id,
        name: draft.name,
        days: draft.days,
        slots: draft.slots,
        cells: draft.cells,
        notes: draft.notes,
      });
      toast.success("Template saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const trySave = () => {
    if (draft.locked && draft.usage.some(u => u.live)) {
      const liveNames = draft.usage.filter(u => u.live).map(u => u.plan_name).join(", ");
      setShowLockConfirm(liveNames);
    } else {
      doSave();
    }
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button onClick={onClose} className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink mb-4">
          <ArrowLeft size={14} /> Back to templates
        </button>

        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Weekly Menu Template</div>
            <input data-testid="tpl-builder-name" value={draft.name} onChange={e => setDraft(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g., Spring Term — Junior Wing"
              className="font-heading text-3xl font-extrabold tracking-tight text-ink mt-1 bg-transparent border-b-2 border-cream-300 focus:border-brand focus:outline-none w-full max-w-2xl" />
          </div>
          <button data-testid="tpl-builder-save" onClick={trySave} disabled={saving}
            className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 disabled:opacity-50">
            {saving ? "Saving…" : "Save template"}
          </button>
        </div>

        {draft.locked && (
          <div className="mb-5 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3">
            <Lock size={18} className="text-amber-700 mt-0.5" />
            <div className="text-sm">
              <strong className="text-amber-900">Template is currently in use by a live term.</strong>
              <div className="text-amber-800 mt-0.5">
                Bound to: {draft.usage.map(u => `${u.plan_name}${u.live ? " (LIVE)" : ""}`).join(", ")}
              </div>
            </div>
          </div>
        )}

        {/* Day/slot selectors */}
        <div className="bg-white rounded-2xl border border-border p-4 mb-5 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase font-bold text-ink-500 mb-1">Days</div>
            <div className="flex gap-1">
              {ALL_DAYS.map(d => (
                <button key={d} onClick={() => toggleDay(d)} data-testid={`tpl-day-${d}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase ${draft.days.includes(d) ? "bg-ink text-white" : "bg-cream-200 text-ink-700"}`}>
                  {DAY_LABELS[d]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold text-ink-500 mb-1">Slots</div>
            <div className="flex gap-1">
              {ALL_SLOTS.map(s => (
                <button key={s} onClick={() => toggleSlot(s)} data-testid={`tpl-slot-${s}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${draft.slots.includes(s) ? "bg-ink text-white" : "bg-cream-200 text-ink-700"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grid: rows=slots, cols=days */}
        <div className="bg-white rounded-2xl border border-border p-4 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="w-24 p-2 text-left text-xs uppercase font-bold text-ink-500"></th>
                {draft.days.map(d => (
                  <th key={d} className="p-2 text-left text-xs uppercase font-bold text-ink-500 min-w-[160px]">{DAY_LABELS[d]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {draft.slots.map(slot => (
                <tr key={slot}>
                  <td className="p-2 align-top">
                    <div className="text-xs uppercase font-bold text-ink-700 capitalize sticky left-0">{slot}</div>
                  </td>
                  {draft.days.map(day => (
                    <td key={day} className="p-1 align-top">
                      <CellEditor value={draft.cells[cellKey(day, slot)]} locked={false}
                        onChange={(val) => updateCell(day, slot, val)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showLockConfirm && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-heading text-lg font-extrabold mb-2 flex items-center gap-2">
              <Lock size={18} className="text-amber-600" /> Saving a live menu
            </h3>
            <p className="text-sm text-ink-700 mb-4">
              This template is currently powering live plan(s): <strong>{showLockConfirm}</strong>. Saving will affect students already eating off this menu.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowLockConfirm(false)} className="px-4 py-2 rounded-full text-sm font-semibold bg-cream-200 text-ink-700">Cancel</button>
              <button data-testid="confirm-save-live" onClick={() => { setShowLockConfirm(false); doSave(); }}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-amber-600 text-white">Save anyway</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

export default function AdminWeeklyMenu() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | template object
  const [showInactive, setShowInactive] = useState(false);

  const load = () => {
    setLoading(true);
    const qp = showInactive ? "?includeInactive=true" : "";
    api.get(`/weekly-menu/templates${qp}`)
      .then(r => setTemplates(Array.isArray(r.data) ? r.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showInactive]);

  const onDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/weekly-menu/templates/${tpl.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const onToggleActive = async (tpl) => {
    const next = tpl.active === false ? true : false;
    try {
      await api.patch(`/weekly-menu/templates/${tpl.id}/active`, { active: next });
      toast.success(next ? "Activated" : "Set inactive");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (editing) {
    return <TemplateBuilder tpl={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={load} />;
  }

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Weekly Menu</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Template library</h1>
            <p className="text-sm text-ink-500 mt-1">Reusable weekly grids that subscription plans link to. Only Active templates appear when creating a plan.</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="toggle-inactive" onClick={() => setShowInactive(s => !s)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold uppercase tracking-wider ${showInactive ? "bg-amber-100 text-amber-800" : "bg-cream-200 text-ink-700 hover:bg-cream-300"}`}>
              {showInactive ? "Hide inactive" : "Show inactive"}
            </button>
            <button onClick={() => setEditing("new")} data-testid="new-template-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white font-semibold hover:bg-ink-700">
              <Plus size={14} /> New Template
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-ink-500">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="border-2 border-dashed border-cream-300 rounded-2xl p-12 text-center bg-white/50">
            <ImageIcon size={36} className="mx-auto text-ink-400 mb-3" />
            <h3 className="font-heading text-xl font-bold text-ink mb-2">No templates yet</h3>
            <p className="text-sm text-ink-500 mb-4">Build a weekly grid (days × meal slots) once, then link it to any subscription plan.</p>
            <button onClick={() => setEditing("new")} data-testid="empty-new-template"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600">
              <Plus size={14} /> Create your first template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {templates.map(tpl => (
              <TemplateCard key={tpl.id} tpl={tpl} onEdit={setEditing} onDelete={onDelete} onToggleActive={onToggleActive} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
